import Login from 'models/login';
import Tenant from 'models/tenant';
import { appRoles } from 'services/Auth/appRoles';
import { orgUnitHasSeparateEntra } from 'utils/orgUnit';
import { sortOnString } from 'utils/sorting';
import { SubscriptionTypes } from 'utils/subscription';

//
// Outlook-only org unit helpers. These live here and not in utils/orgUnit because that file is
// shared: copyFromWebClient.bat overwrites src/utils on every sync, which silently deletes
// anything the web project does not have. Only add-in-specific folders survive a sync.
//
// An org unit that is linked to its own Entra directory cannot be accessed from the Outlook
// add-in: Outlook SSO tokens are always issued for the mailbox directory.
//

//Return the environments (top level + org units) the user can create tasks in from Outlook.
//Mirrors the filters of the web app tenant list (containers out, licensed-or-org-admin) and
//additionally excludes org units with their own Entra directory and other organizations where
//the user is a guest. The current login tenant is always included so the picker can show it.
export const getSelectableOrgUnitsForOutlook = (login: Login): Tenant[] => {
  const tenants = login.tenants ?? [];
  const current = tenants.find((t) => t.id === login.tenantId);
  if (!current) {
    return [];
  }

  const isOrgAdmin = login.userSystemRoles?.includes(appRoles.OrgAdmin) === true;

  const selectable = tenants
    .filter((t) => t.topLevelParentTenantId === current.topLevelParentTenantId)
    .filter((t) => t.subscriptionType !== SubscriptionTypes.Container)
    .filter((t) => !orgUnitHasSeparateEntra(t, tenants))
    .filter((t) => t.currentUserLicensed === true || isOrgAdmin || t.id === login.tenantId);

  //show the top level environment first, then the org units sorted on name
  selectable.sort((a, b) => {
    if (!a.parentId) return -1;
    if (!b.parentId) return 1;

    return sortOnString(a.name, b.name);
  });

  return selectable;
};

//Whether the tenant the back-end logged the user into is an org unit that Outlook can operate on.
export const isOrgUnitUsableFromOutlook = (login: Login): boolean => {
  const tenants = login.tenants ?? [];
  const current = tenants.find((t) => t.id === login.tenantId);
  if (!current || !current.parentId) {
    return false;
  }

  return !orgUnitHasSeparateEntra(current, tenants);
};
